'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Search, X } from 'lucide-react'
import { useLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import type { ArticleSortOrder } from '@/lib/blog/article-types'

const SEARCH_DEBOUNCE_MS = 400

interface BlogToolbarProps {
  currentSort: ArticleSortOrder
  /** Show the search box; the value is kept in the ?q= URL param. */
  showSearch?: boolean
  currentQuery?: string
}

export function BlogToolbar({ currentSort, showSearch = false, currentQuery = '' }: BlogToolbarProps) {
  const { t } = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const navigate = (updates: Record<string, string | null>, resetPage: boolean) => {
    const params = new URLSearchParams(searchParams.toString())
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) params.delete(key)
      else params.set(key, value)
    }
    if (resetPage) params.delete('page')
    const qs = params.toString()
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
  }

  const [searchValue, setSearchValue] = useState(currentQuery)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep the box in sync when the URL changes from outside (back button, category link)
  useEffect(() => {
    setSearchValue(currentQuery)
  }, [currentQuery])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  // A new search starts from page 1 and replaces any tag filter
  const submitSearch = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = value.trim()
    if (trimmed === currentQuery.trim()) return
    navigate({ q: trimmed || null, tags: null }, true)
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => submitSearch(value), SEARCH_DEBOUNCE_MS)
  }

  const baseBtn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors duration-200'
  const active = 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
  const inactive = 'text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {showSearch ? (
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            submitSearch(searchValue)
          }}
          className="relative w-full sm:max-w-xs"
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('blog.search_placeholder')}
            aria-label={t('blog.search_placeholder')}
            maxLength={100}
            className="h-9 w-full rounded-lg border border-border/70 bg-card/90 pl-9 pr-9 text-sm placeholder:text-muted-foreground/70 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 [&::-webkit-search-cancel-button]:hidden"
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => {
                setSearchValue('')
                submitSearch('')
              }}
              aria-label={t('blog.search_clear')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </form>
      ) : (
        <span />
      )}
      <div className="ml-auto inline-flex overflow-hidden rounded-lg border border-border/70 bg-card/90">
        <button
          type="button"
          onClick={() => navigate({ sort: 'newest' }, true)}
          className={cn(baseBtn, currentSort === 'newest' ? active : inactive)}
          aria-pressed={currentSort === 'newest'}
        >
          <ArrowDownWideNarrow className="h-4 w-4" />
          {t('blog.sort_newest')}
        </button>
        <button
          type="button"
          onClick={() => navigate({ sort: 'oldest' }, true)}
          className={cn(baseBtn, currentSort === 'oldest' ? active : inactive)}
          aria-pressed={currentSort === 'oldest'}
        >
          <ArrowUpNarrowWide className="h-4 w-4" />
          {t('blog.sort_oldest')}
        </button>
      </div>
    </div>
  )
}
